import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { CHART_COLORS, getAgChartTheme, getChartColors, formatPercent, formatNumber, formatCurrency } from './chartTheme';
import { useTheme } from '../../../stores/ThemeContext';
import { startOfWeek, format, getISOWeek, getISOWeekYear, parseISO } from 'date-fns';
/**
 * @typedef {Object} Trade
 * @property {number} profit - Trade profit/loss
 * @property {string} [trade_date] - Trade date (ISO string)
 * @property {string} [closeTime] - Close time (ISO string)
 */

/**
 * @typedef {Object} WeeklyDataPoint
 * @property {string} week - Week label (e.g., "2024-W01")
 * @property {Date} weekStart - Start date of the week
 * @property {string} weekLabel - Formatted week label
 * @property {number} trades - Number of trades
 * @property {number} wins - Number of winning trades
 * @property {number} losses - Number of losing trades
 * @property {number} winRate - Win rate percentage
 * @property {number} totalProfit - Total profit for the week
 * @property {number} avgProfit - Average profit per trade
 */

/**
 * Breakeven threshold for categorizing trades
 */
const BREAKEVEN_THRESHOLD = 0.01;

/**
 * Get ISO week string from date
 * @param {Date} date
 * @returns {string} - Week string (e.g., "2024-W01")
 */
const getWeekKey = (date) => {
  const year = getISOWeekYear(date);
  const week = getISOWeek(date);
  return `${year}-W${String(week).padStart(2, '0')}`;
};

/**
 * Get week label for display
 * @param {Date} date
 * @returns {string} - Formatted week label
 */
const getWeekLabel = (date) => {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday start
  return format(weekStart, 'MMM d');
};

/**
 * Trade Volume + Win Rate Combo Chart
 * Shows trade volume (bars) and win rate (line) grouped by ISO week
 *
 * @param {Object} props
 * @param {Trade[]} props.trades - Array of trade objects
 * @param {string} [props.title='Weekly Trading Activity'] - Chart title
 * @param {number} [props.height=350] - Chart height in pixels
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {number} [props.maxWeeks=12] - Maximum weeks to display
 *
 * @example
 * <VolumeWinRateComboChart
 *   trades={trades}
 *   title="Weekly Performance Overview"
 *   height={400}
 * />
 */
const VolumeWinRateComboChart = ({
  trades = [],
  title = 'Weekly Trading Activity',
  height = 350,
  className = '',
  maxWeeks = 12,
}) => {
  const { isDark } = useTheme();
  const chartColors = getChartColors(isDark);
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height });

  // Handle responsive resizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setDimensions({ width, height });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  // Compute weekly aggregated data
  const chartData = useMemo(() => {
    if (!trades || trades.length === 0) {
      return [];
    }

    // Group trades by ISO week
    const weeklyGroups = {};

    trades.forEach((trade) => {
      const dateStr = trade.trade_date || trade.closeTime;
      if (!dateStr) return;

      let date;
      try {
        date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
        if (isNaN(date.getTime())) return;
      } catch {
        return;
      }

      const weekKey = getWeekKey(date);
      const weekStart = startOfWeek(date, { weekStartsOn: 1 });

      if (!weeklyGroups[weekKey]) {
        weeklyGroups[weekKey] = {
          week: weekKey,
          weekStart,
          weekLabel: getWeekLabel(date),
          trades: 0,
          wins: 0,
          losses: 0,
          totalProfit: 0,
        };
      }

      const profit = Number(trade.profit) || 0;
      weeklyGroups[weekKey].trades++;
      weeklyGroups[weekKey].totalProfit += profit;

      if (profit > BREAKEVEN_THRESHOLD) {
        weeklyGroups[weekKey].wins++;
      } else if (profit < -BREAKEVEN_THRESHOLD) {
        weeklyGroups[weekKey].losses++;
      }
    });

    // Convert to array and calculate derived values
    /** @type {WeeklyDataPoint[]} */
    let data = Object.values(weeklyGroups)
      .map((week) => ({
        ...week,
        winRate: week.trades > 0 ? (week.wins / week.trades) * 100 : 0,
        avgProfit: week.trades > 0 ? week.totalProfit / week.trades : 0,
      }))
      .sort((a, b) => a.weekStart - b.weekStart);

    // Limit to most recent weeks
    if (data.length > maxWeeks) {
      data = data.slice(-maxWeeks);
    }

    return data;
  }, [trades, maxWeeks]);

  // Chart options
  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    data: chartData,
    title: {
      text: title,
      fontSize: 16,
      fontWeight: 600,
      color: chartColors.text,
    },
    series: [
      // Bar series for trade volume
      {
        type: 'bar',
        xKey: 'weekLabel',
        yKey: 'trades',
        yName: 'Trades',
        fill: CHART_COLORS.secondary,
        stroke: CHART_COLORS.secondary,
        fillOpacity: 0.7,
        tooltip: {
          renderer: ({ datum }) => ({
            title: `Week of ${datum.weekLabel}`,
            content: `Trades: ${datum.trades}\nWins: ${datum.wins} | Losses: ${datum.losses}\nWin Rate: ${formatPercent(datum.winRate)}\nTotal P/L: ${formatCurrency(datum.totalProfit)}\nAvg P/L: ${formatCurrency(datum.avgProfit)}`,
          }),
        },
        highlightStyle: {
          item: {
            fillOpacity: 1,
          },
        },
      },
      // Line series for win rate
      {
        type: 'line',
        xKey: 'weekLabel',
        yKey: 'winRate',
        yName: 'Win Rate',
        stroke: CHART_COLORS.win,
        strokeWidth: 3,
        marker: {
          enabled: true,
          fill: CHART_COLORS.win,
          stroke: CHART_COLORS.win,
          size: 6,
        },
        tooltip: {
          renderer: ({ datum }) => ({
            title: `Week of ${datum.weekLabel}`,
            content: `Win Rate: ${formatPercent(datum.winRate)}\nWins: ${datum.wins}/${datum.trades}`,
          }),
        },
        highlightStyle: {
          item: {
            fill: CHART_COLORS.win,
            strokeWidth: 4,
          },
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        title: {
          text: 'Week',
          color: chartColors.textMuted,
          fontSize: 12,
        },
        label: {
          color: chartColors.textMuted,
          fontSize: 10,
          rotation: chartData.length > 8 ? 45 : 0,
        },
        line: {
          stroke: CHART_COLORS.axis,
        },
        gridLine: {
          style: [{
            stroke: CHART_COLORS.gridLine,
            lineDash: [4, 2],
          }],
        },
      },
      // Left axis for trade count (bars)
      {
        type: 'number',
        position: 'left',
        keys: ['trades'],
        title: {
          text: 'Trade Count',
          color: chartColors.textMuted,
          fontSize: 12,
        },
        label: {
          color: chartColors.textMuted,
          fontSize: 11,
        },
        line: {
          stroke: CHART_COLORS.axis,
        },
        gridLine: {
          style: [{
            stroke: CHART_COLORS.gridLine,
            lineDash: [4, 2],
          }],
        },
      },
      // Right axis for win rate (line)
      {
        type: 'number',
        position: 'right',
        keys: ['winRate'],
        title: {
          text: 'Win Rate (%)',
          color: CHART_COLORS.win,
          fontSize: 12,
        },
        min: 0,
        max: 100,
        label: {
          color: CHART_COLORS.win,
          fontSize: 11,
          formatter: ({ value }) => `${value}%`,
        },
        line: {
          stroke: CHART_COLORS.win,
        },
        gridLine: {
          enabled: false,
        },
      },
    ],
    legend: {
      enabled: true,
      position: 'bottom',
      item: {
        marker: {
          shape: 'square',
          size: 12,
        },
        label: {
          color: chartColors.text,
          fontSize: 12,
        },
        paddingX: 16,
        paddingY: 8,
      },
    },
    background: {
      fill: CHART_COLORS.background,
    },
  }), [chartData, title, isDark]);

  // Empty state
  if (!trades || trades.length === 0) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No trade data</p>
          <p className="text-sm">Add trades to see weekly activity</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`w-full ${className}`} style={{ height }}>
      <AgCharts ref={chartRef} options={options} />
    </div>
  );
};

export default VolumeWinRateComboChart;
